/**
 * Calendar-only YYYY-MM-DD math in UTC to avoid timezone shifts from
 * `new Date('YYYY-MM-DD')` + local `setMonth` / `setDate` (off-by-one / inverted ranges).
 */

export function parseYmdUTC(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(str || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo, d));
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

export function formatYmdUTC(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function addMonthsYmdUtc(ymd, deltaMonths) {
  const d = parseYmdUTC(ymd);
  if (d == null) return '';
  const y = d.getUTCFullYear();
  const mo = d.getUTCMonth();
  const day = d.getUTCDate();
  return formatYmdUTC(new Date(Date.UTC(y, mo + deltaMonths, day)));
}

export function addDaysYmdUtc(ymd, deltaDays) {
  const d = parseYmdUTC(ymd);
  if (d == null) return '';
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return formatYmdUTC(d);
}

/**
 * Last calendar day of cover for a period starting at startYmd (inclusive span).
 * e.g. 2026-05-11 + 1 Month → 2026-06-10
 */
export function inclusiveEndForCoverPeriod(startYmd, period) {
  if (!startYmd || !period) return '';
  if (period === '1 Year' || period === '1 Year (50%)') {
    return addDaysYmdUtc(addMonthsYmdUtc(startYmd, 12), -1);
  }
  if (period === '6 Months' || period === '6 Month') {
    return addDaysYmdUtc(addMonthsYmdUtc(startYmd, 6), -1);
  }
  if (period === '1 Month') {
    return addDaysYmdUtc(addMonthsYmdUtc(startYmd, 1), -1);
  }
  return '';
}

/** Compare YYYY-MM-DD strings: -1 if a<b, 0 if equal, 1 if a>b; 0 if either invalid. */
export function compareYmd(a, b) {
  const da = parseYmdUTC(a);
  const db = parseYmdUTC(b);
  if (!da || !db) return 0;
  const t = da.getTime() - db.getTime();
  return t < 0 ? -1 : t > 0 ? 1 : 0;
}

/** Local calendar YYYY-MM-DD (browser / Node with Date local). Use for agent-facing dates, not UTC midnight. */
export function getTodayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Earliest TPO second-50% extension start from the portal first-segment 1-month certificate.
 * - If that certificate has already ended: today (local) is allowed.
 * - If it is still active (or ends today): day after certificate end, or tomorrow — whichever is later.
 * - Without first-segment start on file: tomorrow (legacy fallback).
 */
export function getTpoExtendEarliestStartYmd(
  firstSegmentStartYmd,
  todayYmd = getTodayYmdLocal()
) {
  const tomorrow = addDaysYmdUtc(todayYmd, 1);
  if (!firstSegmentStartYmd) return tomorrow;
  const certEnd = inclusiveEndForCoverPeriod(firstSegmentStartYmd, '1 Month');
  if (!certEnd) return tomorrow;
  if (compareYmd(certEnd, todayYmd) < 0) return todayYmd;
  const dayAfterCertEnd = addDaysYmdUtc(certEnd, 1);
  return compareYmd(dayAfterCertEnd, tomorrow) > 0 ? dayAfterCertEnd : tomorrow;
}

/**
 * TPO second-50% extension selectable window.
 * Latest is min(today + 30d, annualEnd).
 * @returns {{ earliest: string, latest: string, empty: boolean } | null}
 */
export function getTpoExtendStartWindow(
  annualEndYmd,
  todayYmd = getTodayYmdLocal(),
  firstSegmentStartYmd = null
) {
  if (!annualEndYmd || !todayYmd) return null;
  const earliest = getTpoExtendEarliestStartYmd(firstSegmentStartYmd, todayYmd);
  const forwardCap = addDaysYmdUtc(todayYmd, 30);
  const latest = compareYmd(forwardCap, annualEndYmd) <= 0 ? forwardCap : annualEndYmd;
  const empty = compareYmd(earliest, latest) > 0;
  return { earliest, latest, empty };
}

export function isTpoExtendStartAllowed(
  startYmd,
  annualEndYmd,
  todayYmd = getTodayYmdLocal(),
  firstSegmentStartYmd = null
) {
  if (!startYmd || !annualEndYmd) return false;
  if (compareYmd(startYmd, annualEndYmd) > 0) return false;
  const w = getTpoExtendStartWindow(annualEndYmd, todayYmd, firstSegmentStartYmd);
  if (!w || w.empty) return false;
  return compareYmd(startYmd, w.earliest) >= 0 && compareYmd(startYmd, w.latest) <= 0;
}

/**
 * Inclusive span from startYmd through endYmdInclusive (both calendar days in UTC).
 * Used for TPO extension copy: extension start → annual policy last day.
 */
export function inclusiveSpanMonthsAndDays(startYmd, endYmdInclusive) {
  if (!startYmd || !endYmdInclusive) return { months: 0, days: 0 };
  if (compareYmd(startYmd, endYmdInclusive) > 0) return { months: 0, days: 0 };
  let months = 0;
  for (;;) {
    const boundary = addMonthsYmdUtc(startYmd, months + 1);
    if (!boundary || compareYmd(boundary, endYmdInclusive) > 0) break;
    months += 1;
    if (months > 600) break;
  }
  const spanStart = addMonthsYmdUtc(startYmd, months);
  const da = parseYmdUTC(spanStart);
  const db = parseYmdUTC(endYmdInclusive);
  if (!da || !db) return { months, days: 0 };
  const days = Math.round((db.getTime() - da.getTime()) / 86400000) + 1;
  return { months, days: Math.max(0, days) };
}
